import React, { useEffect, useMemo, useState } from "react";
const STUDY = {
  HRI_CS02: "HRI",
  GCU_CS03: "GCU",
  INDIA_INTERNAL: "INDIA INTERNAL",
};

const LOCATION_TAGS = {
  "Eye Open & Close": "Eye Open & Close",
  Alpha: "Alpha",
  SSVEP: "SSVEP",
};
const SUBJECT_GROUPS = {
  1: "Cohort",
  2: "Control",
};
export default function RecordingAdminPatientFilterCard({
  onApplyFilter,
  currentFilters = {},
  searchValue = "",
  onSearchChange,
}) {
  const [preset, setPreset] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [studyType, setStudyType] = useState("");
  const [locationTag, setLocationTag] = useState("");
  const [subjectGroup, setSubjectGroup] = useState("");

  const yyyyMmDd = (d) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

  const computePresetRange = (value) => {
    const now = new Date();
    if (value === "all") {
      return { from: "", to: "" };
    }
    if (value === "this_year_to_date") {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: yyyyMmDd(start), to: yyyyMmDd(now) };
    }
    if (value === "last_year") {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { from: yyyyMmDd(start), to: yyyyMmDd(end) };
    }
    if (value === "last_month") {
      const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endLastMonth = new Date(firstDayThisMonth - 1);
      const startLastMonth = new Date(
        endLastMonth.getFullYear(),
        endLastMonth.getMonth(),
        1,
      );
      return { from: yyyyMmDd(startLastMonth), to: yyyyMmDd(endLastMonth) };
    }
    // custom
    return { from: dateFrom || "", to: dateTo || "" };
  };

  useEffect(() => {
    if (preset !== "custom") {
      const { from, to } = computePresetRange(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, [preset]);

  useEffect(() => {
    // seed from current filters if provided
    if (currentFilters) {
      if (currentFilters.dateFrom) setDateFrom(currentFilters.dateFrom);
      if (currentFilters.dateTo) setDateTo(currentFilters.dateTo);
      if (currentFilters.studyType) setStudyType(currentFilters.studyType);
      if (currentFilters.locationTag)
        setLocationTag(currentFilters.locationTag);
      if (currentFilters.subjectGroup)
        setSubjectGroup(currentFilters.subjectGroup);
    }
  }, [currentFilters]);

  const handleRunReport = () => {
    const payload = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      studyType: studyType || undefined,
      locationTag: locationTag || undefined,
      subjectGroup: subjectGroup || undefined,
    };

    onApplyFilter(payload);
  };

  const handleClearFilters = () => {
    setPreset("");
    setDateFrom("");
    setDateTo("");
    setStudyType("");
    setLocationTag("");
    setSubjectGroup("");
    onSearchChange?.({ target: { value: "" } });
    onApplyFilter({});
  };

  const isCustom = preset === "custom";

  return (
    <div className="filter-card">
      <div className="filter-card-header">
        <div className="filter-card-title">Filters</div>
      </div>

      <div className="filter-row filter-row-top">
        <div className="filter-group">
          <div className="filter-label">Date Range</div>
          <div className="filter-date-controls">
            <select
              className="filter-input"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="all">All</option>
              <option value="this_year_to_date">This Year-to-date</option>
              <option value="last_year">Last Year</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={!isCustom}
            />
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={!isCustom}
            />
          </div>
        </div>
        <div className="filter-group">
          <div className="filter-label">Subject</div>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by ID..."
            value={searchValue}
            onChange={onSearchChange}
          />
        </div>
        <div className="filter-group">
          <div className="filter-label">Study</div>
          <select
            className="filter-input"
            value={studyType}
            onChange={(e) => setStudyType(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(STUDY).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Location Tag</div>
          <select
            className="filter-input"
            value={locationTag}
            onChange={(e) => setLocationTag(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(LOCATION_TAGS).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <div className="filter-label">Subject Group</div>
          <select
            className="filter-input"
            value={subjectGroup}
            onChange={(e) => setSubjectGroup(e.target.value)}
          >
            <option value="">All</option>
            {Object.values(SUBJECT_GROUPS).map((label) => (
              <option value={label} key={label}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-actions">
          <button
            className="clear-filter-btn"
            type="button"
            onClick={handleClearFilters}
          >
            Clear filters
          </button>
          <button
            className="run-report-btn"
            type="button"
            onClick={handleRunReport}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
